<div
    ref:container
    class="container"
    style="width:{ width }; height:{ height }; background-image:url({ poster })"
>

    <video
        ref:video
        autoplay preload loop muted playsinline
        on:error="set({ hasError: true })"
        on:canplay="startPlayback()"
        class="{ classList.join(' ') }"
    >
        { #if bestFit.sources }
            { #each Object.entries(bestFit.sources) as [index, source] }
                { #if index < bestFit.sources.length - 1 }
                    <source src="{ source.url }" type="{ source.type }"     />
                { :else }
                    <source src="{ source.url }" type="{ source.type }" on:error="set({ hasError: true })" />
                { /if }
            { /each }
        { /if }
    </video>

</div>

<style>

    .container {
        position: relative;
        overflow: hidden;

        background-size: cover;
        background-repeat: no-repeat;
        background-position: center;
    }

    video {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        opacity: 0;
        transition: .5s opacity ease-in-out;
    }

    video.is-visible {
        opacity: 1;
    }

    video.ratio-smaller {
        width: 100%;
    }

    video.ratio-larger {
        height: 100%;
    }

</style>

<script>
    import 'core-js/features/object/entries';

    export default {

        data() {
            return {
                width: '100%',
                height: 'auto',

                poster: null,
                variants: [],

                isPlaying: false,
                hasError: false,
                ratioModifier: 'ratio-smaller',
                bestFit: [],
            };
        },

        computed: {
            classList: ({ isPlaying, ratioModifier }) => {
                let classList = [];

                if(isPlaying) {
                    classList.push('is-visible');
                }

                classList.push(ratioModifier);

                return classList;
            },
        },

        oncreate() {
            window.addEventListener('resize', () => {
                let oldBestFit = this.get().bestFit;
                let newBestFit = this.getBestFit();

                this.set({
                    bestFit: newBestFit,
                    ratioModifier: this.getRatioModifier(newBestFit)
                });

                if(!oldBestFit || oldBestFit.id !== newBestFit.id) {
                    setTimeout(() => this.refs.video.load(), 0);
                }
            });

            let event = new Event('resize');
            window.dispatchEvent(event);
        },

        methods: {

            startPlayback() {
                this.refs.video.addEventListener('play', () => {
                    this.set({
                        isPlaying: true,
                    });
                });

                this.refs.video.play();
            },

            /*
             * Returns the variant of the video that covers the
             * container most effectively, i. e. the video that
             * needs to be scaled the least.
             */
            getBestFit() {
                let containerDimensions = this.getEffectiveDimensions();
                let containerRatio = this.getContainerRatio();

                let variants = this.get().variants;
                let bestFit = null;

                for(let variant of variants) {
                    let variantRatio = variant.width / variant.height;

                    if(containerRatio > variantRatio) {
                        variant.density = containerDimensions.width / variant.width;
                    } else {
                        variant.density = containerDimensions.height / variant.height;
                    }

                    if(!bestFit) {
                        bestFit = variant;
                    } else {
                        let variantDiff = Math.abs(variant.density - 1);
                        let bestFitDiff = Math.abs(bestFit.density - 1);

                        if(variantDiff < bestFitDiff) {
                            // If variant needs to be scaled up/down less than the
                            // current best fit, it fits better
                            bestFit = variant;
                        } else if(variantDiff === bestFitDiff) {
                            // If the variant needs to be scaled to the exact same
                            // extend as the current bestFit fit, it’s the more efficient
                            // choice if the video size (or total number of pixels)
                            // is smaller.
                            let variantArea = variant.width * variant.height;
                            let bestFitArea = bestFit.width * bestFit.height;

                            if(variantArea < bestFitArea) {
                                bestFit = variant;
                            }
                        }
                    }
                }

                return bestFit;
            },

            /*
             * Returns the dimensions of the video container
             * adjusted for the device’s pixel density facotr
             */
            getEffectiveDimensions() {
                let pixelDensity = window.devicePixelRatio || 1;
                let rect = this.refs.container.getBoundingClientRect();
                let width = rect.width;
                let height = rect.height;

                return {
                    width: pixelDensity * width,
                    height: pixelDensity * height,
                }
            },

            getContainerRatio() {
                let containerDimensions = this.getEffectiveDimensions();
                return containerDimensions.width / containerDimensions.height;
            },

            getRatioModifier(bestFit) {
                bestFit = bestFit || this.get().bestFit;
                let containerRatio = this.getContainerRatio();

                if(bestFit.width / bestFit.height > containerRatio) {
                    return containerRatio > 1 ? 'ratio-larger' : 'ratio-smaller';
                } else {
                    return containerRatio > 1 ? 'ratio-smaller' : 'ratio-larger';
                }
            }

        },

    }

</script>
